/**
 * Which cycle the panel is looking at. One store per domain — this one knows
 * the organization's cycles and which of them is picked, nothing else.
 *
 * Not remembered across reloads on purpose: the default is the phase everyone
 * is working on, and coming back to a one-person extension a day later, with
 * nineteen leaders apparently gone, would read as data loss.
 */
import { create } from 'zustand';
import type { CycleSummaryDto } from '@genoma/shared';

import { setCycleScope } from '../services/api.ts';
import { fetchCycles } from '../services/dashboard.service.ts';

type CycleState = {
  cycles: CycleSummaryDto[];
  /** Null until the list arrives; afterwards always a cycle id. */
  selectedId: string | null;
  error: string | null;
  load: () => Promise<void>;
  select: (cycleId: string) => void;
};

export const useCycleStore = create<CycleState>((set, get) => ({
  cycles: [],
  selectedId: null,
  error: null,

  load: async () => {
    try {
      const { cycles } = await fetchCycles();
      const current = get().selectedId;
      const kept = cycles.some((cycle) => cycle.id === current) ? current : null;
      const selectedId =
        kept ?? cycles.find((cycle) => cycle.isDefault)?.id ?? cycles[0]?.id ?? null;
      set({ cycles, selectedId, error: null });
    } catch (caught) {
      // Without the list the panel still works on the server's default, so
      // this is shown as a missing selector rather than a broken screen.
      set({ error: caught instanceof Error ? caught.message : 'Could not load cycles' });
    }
  },

  select: (cycleId) => {
    const picked = get().cycles.find((cycle) => cycle.id === cycleId);
    if (!picked) return;
    // The default travels without a parameter, exactly as before the selector.
    setCycleScope(picked.isDefault ? null : picked.id);
    set({ selectedId: picked.id });
  },
}));
